<template>
	<v-dialog v-model="dialog" max-width="900px" persistent scrollable class="round-card">
		<v-card class="background-2 round-card">
			<v-card-title class="pa-3 background-1">
				<p class="pl-2 pl-xl-3">Collega Conversazione</p>
				<v-spacer></v-spacer>
				<v-tooltip left>
					<template v-slot:activator="{ on: tooltip }">
						<v-btn v-on="{ ...tooltip }" icon @click="close">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</template>
					<span>Chiudi</span>
				</v-tooltip>
			</v-card-title>
			<v-divider></v-divider>

			<v-card-text class="pa-4">
				<v-container fluid>
					<!-- Step 1: Seleziona tipo anagrafica, entità e azione -->
					<v-row v-if="currentStep === 1">
						<v-col cols="12">
							<h3 class="mb-4">Tipo di Anagrafica</h3>
							<v-radio-group v-model="businessType" row>
								<v-radio
									v-for="type in availableBusinessTypes"
									:key="type.value"
									:label="type.label"
									:value="type.value"
								></v-radio>
							</v-radio-group>
						</v-col>

						<v-col cols="12" class="mt-4" v-if="canLinkContact">
							<h3 class="mb-4">Collega a</h3>
							<v-radio-group v-model="entityType" row>
								<v-radio :label="businessConfig.label" value="company"></v-radio>
								<v-radio label="Contatto" value="contact"></v-radio>
							</v-radio-group>
						</v-col>

						<v-col cols="12" class="mt-4">
							<h3 class="mb-4">Azione</h3>
							<v-radio-group v-model="action" row>
								<v-radio label="Collega Esistente" value="link"></v-radio>
								<v-radio label="Crea Nuovo" value="create"></v-radio>
							</v-radio-group>
						</v-col>
					</v-row>

					<!-- Step 2a: Collega esistente - anagrafica ed eventuale contatto -->
					<v-row v-if="currentStep === 2 && action === 'link'">
						<v-col cols="12">
							<h3 class="mb-4">Seleziona {{ businessConfig.label }}</h3>
							<v-autocomplete
								v-model="selectedCompany"
								:items="companies"
								:search-input.sync="companySearch"
								:loading="loadingCompanies"
								:label="'Cerca ' + businessConfig.label"
								item-text="text"
								item-value="value"
								item-disabled="barred"
								return-object
								outlined
								dense
								clearable
								no-filter
								:menu-props="{maxHeight: 300}"
								@change="onCompanySelected"
							>
								<template v-slot:item="data">
									<template v-if="typeof data.item !== 'object'">
										<v-list-item-content v-text="data.item"></v-list-item-content>
									</template>
									<template v-else>
										<v-list-item-avatar
											size="32px"
											style="min-width: 32px; width: 32px; height:32px; border-radius: 50%"
											class="mr-4"
											:color="data.item.color ? data.item.color : 'primary'">
											<v-icon class="fs18i" :color="data.item.color ? data.item.color : 'white'">{{ data.item.icon }}</v-icon>
										</v-list-item-avatar>
										<v-list-item-content>
											<v-list-item-title>{{ data.item.text }}</v-list-item-title>
											<v-list-item-subtitle>
												<span v-for="(key, k) in ['subtitle']" :key="k">{{$trans(data.item[key])}}&nbsp;
												</span>
											</v-list-item-subtitle>
										</v-list-item-content>
									</template>
								</template>
								<template v-slot:no-data>
									<v-list-item>
										<v-list-item-content>
											<v-list-item-title v-if="companySearch">
												Nessun risultato per "{{ companySearch }}"
											</v-list-item-title>
											<v-list-item-title v-else>
												Inizia a digitare per cercare
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</template>
							</v-autocomplete>
						</v-col>

						<v-col cols="12" v-if="entityType === 'contact' && selectedCompany">
							<h3 class="mb-4">Seleziona Contatto</h3>
							<v-autocomplete
								v-model="selectedContact"
								:items="contacts"
								:loading="loadingContacts"
								label="Seleziona Contatto"
								item-text="text"
								item-value="value"
								return-object
								outlined
								dense
								clearable
								:menu-props="{maxHeight: 300}"
							>
								<template v-slot:no-data>
									<v-list-item>
										<v-list-item-content>
											<v-list-item-title>
												Nessun contatto disponibile
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</template>
							</v-autocomplete>
						</v-col>
					</v-row>

					<!-- Step 2b: Crea nuovo - Form con FieldsGenerator -->
					<v-row v-if="currentStep === 2 && action === 'create'">
						<v-col cols="12">
							<h3 class="mb-4">{{ entityType === 'contact' ? 'Nuovo Contatto' : businessConfig.newLabel }}</h3>
							<fields-generator
								v-if="fieldssetLoaded"
								:fieldsset.sync="fieldsset"
								:model.sync="newEntityModel"
								ref="fieldsgenerator"
							></fields-generator>
							<small class="px-4">* Indica i campi obbligatori</small>
						</v-col>
					</v-row>

					<!-- Step 3: Seleziona campo telefono -->
					<v-row v-if="currentStep === 3">
						<v-col cols="12">
							<h3 class="mb-4">Campo Telefono</h3>
							<p class="mb-3">
								Numero da salvare: <strong>{{ conversation.telephone_number }}</strong>
							</p>

							<v-checkbox
								v-model="updatePhone"
								label="Aggiorna il numero di telefono nell'anagrafica"
								hide-details
								class="mb-3"
							></v-checkbox>

							<v-select
								v-if="updatePhone"
								v-model="selectedPhoneField"
								:items="phoneFields"
								:loading="loadingPhoneFields"
								label="Seleziona campo dove salvare il numero"
								item-text="text"
								item-value="value"
								outlined
								dense
								:menu-props="{'offset-y': true}"
							></v-select>
						</v-col>
					</v-row>
				</v-container>
			</v-card-text>

			<v-divider></v-divider>
			<v-card-actions class="px-4 py-3 background-1">
				<v-btn
					v-if="currentStep > 1"
					text
					color="secondary"
					@click="previousStep"
				>
					<v-icon left small>mdi-arrow-left</v-icon>
					Indietro
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn text color="error" @click="close">
					Annulla
				</v-btn>
				<v-btn
					v-if="currentStep < 3"
					text
					color="primary"
					@click="nextStep"
					:disabled="!canProceed"
				>
					Avanti
					<v-icon right small>mdi-arrow-right</v-icon>
				</v-btn>
				<v-btn
					v-else
					text
					color="primary"
					@click="save"
					:loading="saving"
					:disabled="updatePhone && !selectedPhoneField"
				>
					<v-icon left small>mdi-check</v-icon>
					Conferma
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import FieldsGenerator from '@/components/common/FieldsGenerator'

// Anagrafiche a cui collegare una conversazione. Lead e prospect solo come anagrafica,
// clienti e fornitori anche tramite un loro contatto. `permission`, se presente, nasconde
// il tipo a chi non ha il modello tra i permessi
const BUSINESS_TYPES = [
	{
		value: 'company',
		label: 'Azienda',
		newLabel: 'Nuova Azienda',
		search: 'async-companies',
		endpoint: 'companies',
		contacts: { related: 'companies', endpoint: 'companycontacts', entityType: 'contact' }
	},
	{
		value: 'lead',
		label: 'Lead',
		newLabel: 'Nuovo Lead',
		search: 'async-leads',
		endpoint: 'leads',
		permission: 'lead'
	},
	{
		value: 'prospect',
		label: 'Prospect',
		newLabel: 'Nuovo Prospect',
		search: 'async-prospects',
		endpoint: 'prospects',
		permission: 'prospect'
	},
	{
		value: 'supplier',
		label: 'Fornitore',
		newLabel: 'Nuovo Fornitore',
		search: 'async-suppliers',
		endpoint: 'suppliers',
		permission: 'supplier',
		contacts: { related: 'suppliers', endpoint: 'suppliercontacts', entityType: 'suppliercontact', permission: 'suppliercontact' }
	}
]

export default {
	name: 'whatsapp-linking-dialog',

	components: {
		FieldsGenerator
	},

	props: {
		value: {
			type: Boolean,
			default: false
		},
		conversation: {
			type: Object,
			required: true
		}
	},

	data() {
		return {
			currentStep: 1,
			businessType: 'company',
			entityType: 'company',
			action: 'link',

			// Collegamento esistente
			companySearch: '',
			companies: [],
			selectedCompany: null,
			loadingCompanies: false,

			contacts: [],
			selectedContact: null,
			loadingContacts: false,

			// Creazione nuovo
			fieldsset: {},
			newEntityModel: {},
			fieldssetLoaded: false,
			saving: false,

			// Campi telefono
			phoneFields: [],
			selectedPhoneField: null,
			loadingPhoneFields: false,
			updatePhone: true,

			searchDebounceTimer: null
		}
	},

	computed: {
		dialog: {
			get() {
				return this.value
			},
			set(val) {
				this.$emit('input', val)
			}
		},

		availableBusinessTypes() {
			return BUSINESS_TYPES.filter(type => !type.permission || this.$store.getters.checkModelPermissions(type.permission))
		},

		businessConfig() {
			return BUSINESS_TYPES.find(type => type.value === this.businessType)
		},

		canLinkContact() {
			const contacts = this.businessConfig.contacts
			return !!contacts && (!contacts.permission || this.$store.getters.checkModelPermissions(contacts.permission))
		},

		// entity_type per link-conversation: qualsiasi anagrafica è 'company'
		linkedEntityType() {
			return this.entityType === 'contact' ? this.businessConfig.contacts.entityType : 'company'
		},

		// Tipo per i campi telefono: ogni anagrafica ha i suoi custom field
		phoneFieldsType() {
			return this.entityType === 'contact' ? this.businessConfig.contacts.entityType : this.businessType
		},

		createEndpoint() {
			return this.entityType === 'contact' ? this.businessConfig.contacts.endpoint : this.businessConfig.endpoint
		},

		canProceed() {
			if (this.currentStep === 1) {
				return this.businessType && this.entityType && this.action
			}

			if (this.currentStep === 2) {
				if (this.action === 'link') {
					if (this.entityType === 'company') {
						return !!this.selectedCompany
					} else {
						return !!this.selectedContact
					}
				} else {
					// Per create, permettiamo di procedere se i campi obbligatori sono compilati
					// La validazione vera sarà fatta al salvataggio
					return true
				}
			}

			return false
		}
	},

	watch: {
		dialog(val) {
			if (val) {
				this.reset()
			}
		},

		businessType() {
			if (!this.canLinkContact) {
				this.entityType = 'company'
			}
		},

		companySearch(val) {
			if (!val || val.length < 2) return

			if (this.searchDebounceTimer) {
				clearTimeout(this.searchDebounceTimer)
			}

			this.searchDebounceTimer = setTimeout(() => {
				this.searchCompanies()
			}, 500)
		},

		action(val) {
			if (val === 'create' && this.currentStep === 2) {
				this.loadFieldsset()
			}
		}
	},

	methods: {
		reset() {
			this.currentStep = 1
			this.businessType = 'company'
			this.entityType = 'company'
			this.action = 'link'
			this.companySearch = ''
			this.companies = []
			this.selectedCompany = null
			this.contacts = []
			this.selectedContact = null
			this.fieldsset = {}
			this.newEntityModel = {}
			this.fieldssetLoaded = false
			this.phoneFields = []
			this.selectedPhoneField = null
			this.updatePhone = true
		},

		close() {
			this.dialog = false
		},

		nextStep() {
			if (!this.canProceed) return

			if (this.currentStep === 1) {
				this.currentStep = 2
				if (this.action === 'create') {
					this.loadFieldsset()
				}
			} else if (this.currentStep === 2) {
				if (this.action === 'create') {
					// Valida il form prima di procedere
					if (this.$refs.fieldsgenerator) {
						this.$refs.fieldsgenerator.$validator.validateAll().then(result => {
							if (result) {
								this.currentStep = 3
								this.loadPhoneFields()
							}
						})
					}
				} else {
					this.currentStep = 3
					this.loadPhoneFields()
				}
			}
		},

		previousStep() {
			if (this.currentStep > 1) {
				if (this.currentStep === 3) {
					// Tornando dallo step 3 allo step 2
					// Reset completo dei dati dello step 3 (selezione campo telefono)
					this.phoneFields = []
					this.selectedPhoneField = null
					this.updatePhone = true

					this.currentStep = 2
				} else if (this.currentStep === 2) {
					// Tornando dallo step 2 allo step 1
					// Reset completo di tutti i dati dello step 2
					this.selectedCompany = null
					this.selectedContact = null
					this.contacts = []
					this.companies = []
					this.companySearch = ''

					// Reset anche per i dati di creazione
					this.fieldsset = {}
					this.newEntityModel = {}
					this.fieldssetLoaded = false

					this.currentStep = 1
				}
			}
		},

		searchCompanies() {
			this.loadingCompanies = true

			this.axios.get(this.businessConfig.search, {
				params: {
					search: this.companySearch
				}
			}).then(response => {
				this.companies = response.data.options
				this.loadingCompanies = false
			}).catch(() => {
				this.loadingCompanies = false
			})
		},

		onCompanySelected() {
			this.contacts = []
			this.selectedContact = null

			if (!this.selectedCompany || this.entityType !== 'contact') return

			this.loadingContacts = true
			this.axios.get(`get-related/${this.businessConfig.contacts.related}/${this.selectedCompany.value}/optionsContacts?lite=1`)
				.then(response => {
					this.contacts = response.data
					this.loadingContacts = false
				})
				.catch(() => {
					this.loadingContacts = false
				})
		},

		loadFieldsset() {
			this.axios.get(`${this.createEndpoint}/create`, {
				params: {
					field_filters: JSON.stringify({ quick_add: true })
				}
			}).then(response => {
				this.fieldsset = response.data.fields
				this.fieldssetLoaded = true

				// Inizializza i valori di default
				if (this.fieldsset.fields) {
					this.fieldsset.fields.forEach(field => {
						if (field.default) {
							this.$set(this.newEntityModel, field.key, field.default)
						}
					})
				}
			}).catch(error => {
				console.error('Errore caricamento fieldsset:', error)
			})
		},

		loadPhoneFields() {
			this.loadingPhoneFields = true

			this.axios.get(`/phone-fields/${this.phoneFieldsType}`)
				.then(response => {
					this.phoneFields = response.data.data
					this.loadingPhoneFields = false

					// Preseleziona il primo campo se disponibile
					if (this.phoneFields.length > 0) {
						this.selectedPhoneField = this.phoneFields[0].value
					}
				})
				.catch(() => {
					this.loadingPhoneFields = false
				})
		},

		async save() {
			this.saving = true

			try {
				let entityId = null

				// Se stiamo creando una nuova entità
				if (this.action === 'create') {
					const createResponse = await this.axios.post(this.createEndpoint, this.newEntityModel)
					entityId = createResponse.data.id
				} else {
					// Entità esistente
					entityId = this.entityType === 'company'
						? this.selectedCompany.value
						: this.selectedContact.value
				}

				// Collega la conversazione
				await this.axios.post('/link-conversation', {
					conversation_id: this.conversation.id,
					entity_type: this.linkedEntityType,
					entity_id: entityId,
					phone_field: this.selectedPhoneField,
					update_phone: this.updatePhone
				})

				this.$store.commit('setSnackbar', {
					value: true,
					color: 'success',
					text: 'Conversazione collegata con successo'
				})

				this.$emit('linked')
				this.close()

			} catch (error) {
				console.error('Errore durante il salvataggio:', error)
				this.$store.commit('setSnackbar', {
					value: true,
					color: 'error',
					text: error.response?.data?.message || 'Errore durante il collegamento'
				})
			} finally {
				this.saving = false
			}
		}
	},

	beforeDestroy() {
		if (this.searchDebounceTimer) {
			clearTimeout(this.searchDebounceTimer)
		}
	}
}
</script>

<style scoped>
</style>
